'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Textarea Variants
 *
 * Defines visual styles for the Textarea component using design tokens.
 * States: default, error, success, disabled
 */
const textareaVariants = cva(
  // Base styles
  'flex min-h-[80px] w-full rounded-md border px-3 py-2 text-base transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-neutral-100 dark:disabled:bg-neutral-800',
  {
    variants: {
      state: {
        default: 'bg-white text-neutral-900 border-border-primary placeholder:text-neutral-400 hover:border-border-secondary dark:bg-neutral-900 dark:text-white dark:border-border-primary dark:placeholder:text-neutral-500 dark:hover:border-border-secondary',
        error: 'border-error-500 focus-visible:ring-error-500',
        success: 'border-success-500 focus-visible:ring-success-500',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  }
);

export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'>,
    VariantProps<typeof textareaVariants> {
  /** Error message to display below the textarea */
  error?: string;
  /** Success message to display below the textarea */
  success?: string;
  /** Label for the textarea */
  label?: string;
  /** Helper text to display below the textarea */
  helperText?: string;
  /** If true, displays a required indicator */
  required?: boolean;
  /** Custom className */
  className?: string;
}

/**
 * Textarea Component
 *
 * A versatile textarea component with multiple states.
 * Supports error/success states, labels, and helper text.
 * Fully typed with TypeScript and supports form integration.
 *
 * @example
 * ```tsx
 * // Basic textarea
 * <Textarea placeholder="Enter text..." />
 *
 * // Textarea with label
 * <Textarea
 *   label="Description"
 *   placeholder="Enter description..."
 *   required
 * />
 *
 * // Textarea with error state
 * <Textarea
 *   value={description}
 *   state="error"
 *   error="Description is required"
 * />
 *
 * // Textarea with success state
 * <Textarea
 *   state="success"
 *   success="Description saved"
 * />
 *
 * // With helper text
 * <Textarea
 *   label="Rejection Reason"
 *   helperText="Provide a clear reason for rejection (max 500 characters)"
 *   maxLength={500}
 * />
 *
 * // With react-hook-form
 * <Textarea
 *   placeholder="Enter value"
 *   {...register('fieldName')}
 * />
 * ```
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      state = 'default',
      error,
      success,
      label,
      helperText,
      required,
      disabled,
      ...props
    },
    ref
  ) => {
    // Determine state based on error/success if not explicitly set
    const resolvedState = error ? 'error' : success ? 'success' : state;

    return (
      <div className="w-full">
        {/* Label */}
        {label && (
          <label className="mb-2 block text-sm font-medium text-text-primary">
            {label}
            {required && <span className="ml-1 text-error-500">*</span>}
          </label>
        )}

        {/* Textarea Field */}
        <textarea
          className={cn(
            textareaVariants({ state: resolvedState }),
            className
          )}
          ref={ref}
          disabled={disabled}
          {...props}
        />

        {/* Error Message */}
        {error && (
          <p className="mt-1 text-sm text-error-500">
            {error}
          </p>
        )}

        {/* Success Message */}
        {success && (
          <p className="mt-1 text-sm text-success-500">
            {success}
          </p>
        )}

        {/* Helper Text */}
        {helperText && !error && !success && (
          <p className="mt-1 text-sm text-text-secondary">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export { Textarea, textareaVariants };
